/**
 * 🗄️ POI SQLite Service
 * 
 * Ultra-fast POI lookups using SQLite index.
 * Replaces slow osmium PBF scans with O(1) database lookups.
 * 
 * Performance:
 * - osmium scan: ~350ms per lookup, 400MB RAM
 * - SQLite: <0.01ms per lookup, ~10MB RAM
 * 
 * @see scripts/build-poi-index.mjs - Generates the SQLite index
 */
import Database, { Database as DatabaseType } from 'better-sqlite3'
import { createLogger } from '@/common/utils/logger'
import path from 'path'
import fs from 'fs'

const logger = createLogger('poi-sqlite-service')

// --- TYPES ---
export interface POIRecord {
  osm_id: number
  name: string
  category: string
  lat: number
  lng: number
  score: number
  website: string | null
  phone: string | null
  opening_hours: string | null
  description: string | null
  tags_json: string
  metadata_json: string | null
}

export interface POISmartMetadata {
  // Phase 4: Visit Planning
  bestTime?: string
  averageDuration?: string
  crowdLevel?: string
  seasonalNotes?: string

  // Phase 5: Contextual Integration
  nearbyPOIs?: Array<{
    id: string
    name: string
    distance: number
    category: string
  }>
  weatherConsiderations?: string
  accessibilityInfo?: string
  parkingInfo?: string

  // Phase 6: User Experience
  tips?: string[]
  whatToExpect?: string
  photoSpots?: string[]
  localInsights?: string

  // Phase 7: Practical Details
  entryFee?: string
  reservationRequired?: boolean
  languages?: string[]
  facilities?: string[]
}

export interface POIDetails {
  id: string
  osmId: string
  name: string
  category: string
  lat: number
  lng: number
  roadTripScore: number
  website?: string
  phone?: string
  openingHours?: string
  description?: string
  tags: string[]
  coverImage?: string
  source: 'openstreetmap'
  smartMetadata?: POISmartMetadata
}

// --- SERVICE ---
export class POISqliteService {
  private db: DatabaseType | null = null
  private dbPath: string
  private isInitialized = false

  constructor() {
    // Database location - configurable via env
    this.dbPath = process.env.POI_SQLITE_PATH || 
      path.resolve(process.cwd(), 'poi-index.db')
  }

  /**
   * Initialize the database connection.
   * Call this once at app startup.
   */
  async initialize(): Promise<boolean> {
    if (this.isInitialized) return true

    // Auto-download from VPS in dev mode
    if (!fs.existsSync(this.dbPath) && process.env.NODE_ENV !== 'production') {
      logger.info(`POI SQLite database not found locally, attempting download from VPS...`)
      const downloaded = await this.downloadFromVPS()
      if (!downloaded) {
        logger.warn('Failed to download DB from VPS')
        return false
      }
    }

    if (!fs.existsSync(this.dbPath)) {
      logger.warn(`POI SQLite database not found at ${this.dbPath}`)
      logger.warn('Run: node scripts/build-poi-index.mjs --file=europe-roadtrip.osm.pbf')
      return false
    }

    try {
      this.db = new Database(this.dbPath, { readonly: true })

      // Optimize for read performance
      this.db.pragma('cache_size = 10000')  // 10MB cache
      this.db.pragma('mmap_size = 268435456')  // 256MB mmap

      const count = this.db.prepare('SELECT COUNT(*) as count FROM pois').get() as { count: number }
      logger.info(`POI SQLite initialized: ${count.count.toLocaleString()} POIs indexed`)

      this.isInitialized = true
      return true
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      logger.error('Failed to initialize POI SQLite', { error: error.message })
      return false
    }
  }

  /**
   * Get POI by OSM ID.
   * Ultra-fast: <0.01ms per lookup.
   */
  getByOsmId(osmId: string | number): POIDetails | null {
    if (!this.db) {
      if (!this.initialize()) return null
    }

    const numericId = typeof osmId === 'string' ? parseInt(osmId, 10) : osmId
    
    if (isNaN(numericId)) {
      logger.warn(`Invalid OSM ID: ${osmId}`)
      return null
    }

    try {
      const row = this.db!.prepare(
        'SELECT * FROM pois WHERE osm_id = ?'
      ).get(numericId) as POIRecord | undefined

      if (!row) return null

      return this.transformToDetails(row)
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      logger.error(`Error fetching POI ${osmId}`, { error: error.message })
      return null
    }
  }

  /**
   * Search POIs by name (partial match).
   */
  searchByName(query: string, limit = 20): POIDetails[] {
    if (!this.db) {
      if (!this.initialize()) return []
    }

    try {
      const rows = this.db!.prepare(
        'SELECT * FROM pois WHERE name LIKE ? LIMIT ?'
      ).all(`%${query}%`, limit) as POIRecord[]

      return rows.map(row => this.transformToDetails(row))
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      logger.error(`Error searching POIs for "${query}"`, { error: error.message })
      return []
    }
  }

  /**
   * Get POIs by category.
   */
  getByCategory(category: string, limit = 100): POIDetails[] {
    if (!this.db) {
      if (!this.initialize()) return []
    }

    try {
      const rows = this.db!.prepare(
        'SELECT * FROM pois WHERE category = ? ORDER BY score DESC LIMIT ?'
      ).all(category.toLowerCase(), limit) as POIRecord[]

      return rows.map(row => this.transformToDetails(row))
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      logger.error(`Error fetching POIs for category "${category}"`, { error: error.message })
      return []
    }
  }

  /**
   * Get random POIs (for discovery).
   */
  getRandom(limit = 10): POIDetails[] {
    if (!this.db) {
      if (!this.initialize()) return []
    }

    try {
      const rows = this.db!.prepare(
        'SELECT * FROM pois WHERE score >= 6 ORDER BY RANDOM() LIMIT ?'
      ).all(limit) as POIRecord[]

      return rows.map(row => this.transformToDetails(row))
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      logger.error('Error fetching random POIs', { error: error.message })
      return []
    }
  }

  /**
   * Get database stats.
   */
  getStats(): { count: number; categories: Record<string, number> } | null {
    if (!this.db) {
      if (!this.initialize()) return null
    }

    try {
      const count = this.db!.prepare('SELECT COUNT(*) as count FROM pois').get() as { count: number }
      const categories = this.db!.prepare(
        'SELECT category, COUNT(*) as count FROM pois GROUP BY category ORDER BY count DESC'
      ).all() as { category: string; count: number }[]

      return {
        count: count.count,
        categories: Object.fromEntries(categories.map(c => [c.category, c.count]))
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      logger.error('Error fetching POI stats', { error: error.message })
      return null
    }
  }

  /**
   * Transform database row to API response format.
   */
  private transformToDetails(row: POIRecord): POIDetails {
    let tags: string[] = []
    try {
      const tagsObj = JSON.parse(row.tags_json || '{}')
      tags = Object.keys(tagsObj)
    } catch {
      tags = []
    }

    // Parse smart metadata from JSON
    let smartMetadata: POISmartMetadata | undefined = undefined
    if (row.metadata_json) {
      try {
        const parsed = JSON.parse(row.metadata_json)
        smartMetadata = {
          bestTime: parsed.best_time,
          averageDuration: parsed.average_duration,
          crowdLevel: parsed.crowd_level,
          seasonalNotes: parsed.seasonal_notes,
          nearbyPOIs: parsed.nearby_pois,
          weatherConsiderations: parsed.weather_considerations,
          accessibilityInfo: parsed.accessibility_info,
          parkingInfo: parsed.parking_info,
          tips: parsed.tips,
          whatToExpect: parsed.what_to_expect,
          photoSpots: parsed.photo_spots,
          localInsights: parsed.local_insights,
          entryFee: parsed.entry_fee,
          reservationRequired: parsed.reservation_required,
          languages: parsed.languages,
          facilities: parsed.facilities
        }
      } catch (err) {
        logger.warn(`Failed to parse metadata_json for POI ${row.osm_id}`)
      }
    }

    // Generate cover image from category
    const coverImage = `https://source.unsplash.com/800x600/?${row.category.replace('_', ',')}`

    return {
      id: `osm-${row.osm_id}`,
      osmId: String(row.osm_id),
      name: row.name,
      category: row.category,
      lat: row.lat,
      lng: row.lng,
      roadTripScore: row.score,
      website: row.website || undefined,
      phone: row.phone || undefined,
      openingHours: row.opening_hours || undefined,
      description: row.description || undefined,
      tags,
      coverImage,
      source: 'openstreetmap',
      smartMetadata
    }
  }

  /**
   * Download POI database from VPS.
   */
  private async downloadFromVPS(): Promise<boolean> {
    const vpsUrl = process.env.VPS_POI_DB_URL || 'https://vps.myroadtrip.app/poi-index.db'

    try {
      logger.info(`Downloading POI database from ${vpsUrl}...`)

      const response = await fetch(vpsUrl)
      if (!response.ok) {
        logger.error(`Failed to download: HTTP ${response.status}`)
        return false
      }

      const totalSize = parseInt(response.headers.get('content-length') || '0', 10)
      let downloadedSize = 0
      let lastProgress = 0

      // Ensure directory exists
      const dir = path.dirname(this.dbPath)
      if (!fs.existsSync(dir)) {
        fs.mkdirSync(dir, { recursive: true })
      }

      // Stream download with progress
      const fileStream = fs.createWriteStream(this.dbPath)
      const reader = response.body?.getReader()

      if (!reader) {
        logger.error('Failed to get response reader')
        return false
      }

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        fileStream.write(value)
        downloadedSize += value.length

        // Log progress every 10%
        const progress = Math.floor((downloadedSize / totalSize) * 100)
        if (progress >= lastProgress + 10) {
          logger.info(`Download progress: ${progress}% (${(downloadedSize / 1024 / 1024).toFixed(1)}MB / ${(totalSize / 1024 / 1024).toFixed(1)}MB)`)
          lastProgress = progress
        }
      }

      fileStream.end()
      logger.info(`Successfully downloaded POI database (${(downloadedSize / 1024 / 1024).toFixed(1)}MB)`)
      return true
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      logger.error('Failed to download POI database from VPS', { error: error.message })

      // Cleanup partial download
      if (fs.existsSync(this.dbPath)) {
        fs.unlinkSync(this.dbPath)
      }

      return false
    }
  }

  /**
   * Close database connection.
   */
  close(): void {
    if (this.db) {
      this.db.close()
      this.db = null
      this.isInitialized = false
      logger.info('POI SQLite connection closed')
    }
  }

  /**
   * Check if database is available and healthy.
   */
  isHealthy(): boolean {
    if (!this.db) return false
    try {
      this.db.prepare('SELECT 1').get()
      return true
    } catch {
      return false
    }
  }
}

// Singleton instance
export const poiSqliteService = new POISqliteService()
